import React, { Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { BrowserRouter, useSearchParams } from "react-router-dom";
import "./assests/styles/index.css";
import "@shopflo/ui/styles.css";
import { AuthProvider } from "lib/contexts/AuthProvider";
import { UserProvider } from "lib/contexts/UserProvider";
import { CheckoutProvider } from "lib/contexts/CheckoutProvider";
import { MerchantProvider, useMerchantContext } from "lib/contexts/MerchantProvider";
import { addPolyfills } from "lib/core/polyfills";
import Skeleton from "components/common/loaders/Skeleton";
import {
  classNames,
  inIframe,
  isMobile,
  publishPostMessage,
  shouldRedirectToParent,
} from "lib/utils/helpers";
import { PaymentProvider } from "lib/contexts/PaymentProvider";
import { LoaderProvider } from "lib/contexts/LoaderContext";
import useSentry from "lib/hooks/useSentry";
import * as Sentry from "@sentry/react";
import { WishlistProvider } from "lib/contexts/WishlistProvider";
import { eventTypes } from "lib/utils/constants";
import { ErrorType } from "lib/types/error";
import AppWrapper from "lib/wrapper/AppWrapper";
import { TokenProvider } from "lib/contexts/TokenProvider";
import { PopupOrchestratorProvider } from "lib/contexts/PopupOrchestratorProvider";
import { UserConsentProvider } from "lib/contexts/UserConsentProvider";
import { SubscriptionProvider } from "lib/contexts/SubscriptionProvider";

addPolyfills();
// initialiseNRM();
const isPrewarmUrl = (searchParams: URLSearchParams): boolean => searchParams.get("prewarm") === "true";

const searchParams = new URLSearchParams(window.location.search);
const isMarketingPopup = searchParams.get("page") === "marketing-popup";
const container = document.getElementById("root");
const root = createRoot(container!); // createRoot(container!) if you use TypeScript

// Pages that stay inside the iframe even when third party cookies are blocked
const REDIRECT_EXCLUDED_PAGES = [
  "cart",
  "empty-cart",
  "login-status-check",
  "ghost-modal",
  "marketing-popup",
  "experiments-widget",
  "shopify-login",
];

root.render(
  <React.StrictMode>
    <BrowserRouter basename={import.meta.env.VITE_PUBLIC_URL}>
      <UserProvider>
        <TokenProvider>
          <AppWrapper>
            <MerchantProvider>
              <ParentRedirectGate>
                <AppShell>
                  <Suspense fallback={isMarketingPopup ? <></> : <Skeleton />}>
                    <LoaderProvider>
                      <AuthProvider>
                        <UserConsentProvider>
                          <PopupOrchestratorProvider>
                            <CheckoutProvider>
                              <SubscriptionProvider>
                                <WishlistProvider>
                                  <PaymentProvider>
                                    <App />
                                  </PaymentProvider>
                                </WishlistProvider>
                              </SubscriptionProvider>
                            </CheckoutProvider>
                          </PopupOrchestratorProvider>
                        </UserConsentProvider>
                      </AuthProvider>
                    </LoaderProvider>
                  </Suspense>
                </AppShell>
              </ParentRedirectGate>
            </MerchantProvider>
          </AppWrapper>
        </TokenProvider>
      </UserProvider>
    </BrowserRouter>
  </React.StrictMode>,
);

/**
 * When the iframe cannot use third party cookies, checkout is moved to the parent window unless the
 * merchant has disabled the redirect. The merchant attribute is only known after /merchant-apps
 * resolves, so the app is held on the skeleton until then.
 */
function ParentRedirectGate({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const {
    state: { merchant, isMerchantPending },
  } = useMerchantContext();
  const page = searchParams.get("page")?.toLowerCase() ?? "";
  const isRedirectCandidate =
    !isPrewarmUrl(searchParams) && !REDIRECT_EXCLUDED_PAGES.includes(page) && shouldRedirectToParent();

  if (!isRedirectCandidate) return <>{children}</>;
  if (isMerchantPending) return <Skeleton />;
  if (shouldRedirectToParent(merchant?.disableThirdPartyCookieRedirect)) return <RedirectWrapper />;
  return <>{children}</>;
}

function RedirectWrapper() {
  useSentry();

  useEffect(() => {
    const redirectUrl = new URL(document.location.href);
    publishPostMessage(eventTypes.PARENT_REDIRECT, {
      redirectUrl: redirectUrl.href,
      clearCart: false,
    });
  }, []);

  return <Skeleton />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const pageName = searchParams.get("page");
  const isCartPage = ["cart", "empty-cart"].includes(pageName?.toLowerCase() ?? "");
  const isMarketingPopup = pageName === "marketing-popup";
  const isWishlistPage = pageName === "wishlist";
  const isAccountPage = pageName === "account";
  const isMockCheckoutPage = pageName === "mock-checkout";
  const isExperimentWidgetPage = pageName === "experiments-widget";
  const isShopifyLoginPage = pageName === "shopify-login";
  const isProfilePage = pageName === "profile";

  useSentry();

  if (isShopifyLoginPage) {
    return (
      <div className={classNames("h-full w-full rounded-2xl overflow-hidden scrollbar-hide")}>{children}</div>
    );
  }

  if (isMarketingPopup) {
    const isMobileDevice = isMobile();
    return (
      <div
        id="checkout-ui-root"
        className={classNames(
          "relative flex h-full w-full flex-1 justify-center overflow-auto scrollbar-none [&::-webkit-scrollbar]:hidden", //hiding the scrollbar while still allowing the scroll
          isMobileDevice ? "items-end" : "items-center",
        )}>
        <div
          className={classNames(
            "relative flex w-full flex-col",
            isMobileDevice
              ? "h-full self-stretch overflow-y-auto"
              : "h-auto w-[25rem] self-center overflow-visible",
          )}>
          {children}
        </div>
      </div>
    );
  }

  if (isCartPage) {
    return (
      <div
        id="checkout-ui-root"
        className="relative h-full w-full overflow-hidden bg-gray-lightest md:h-[85vh] md:w-[25rem] md:rounded-l-[28px] md:rounded-r-none  md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]">
        {children}
      </div>
    );
  }

  if (isWishlistPage || isAccountPage) {
    return <div className="h-full w-full overflow-hidden bg-transparent">{children}</div>;
  }

  if (isProfilePage) {
    return (
      <div
        id="checkout-ui-root"
        className="relative h-full w-full overflow-x-hidden overflow-y-auto bg-transparent antialiased sm:overflow-y-hidden">
        {children}
      </div>
    );
  }

  if (isMockCheckoutPage) {
    return (
      <div
        id="checkout-ui-root"
        className={classNames(
          "relative h-full w-full bg-gray-lightest md:h-[85vh] md:w-[69rem]",
          isMobile() ? "" : inIframe() ? `rounded-[28px]` : "md:rounded-[28px]",
          "md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]",
        )}>
        {children}
      </div>
    );
  }

  if (isExperimentWidgetPage) {
    return <div className="h-full w-full overflow-hidden bg-transparent">{children}</div>;
  }

  return (
    <div
      id="checkout-ui-root"
      className={classNames(
        "relative h-full w-full overflow-hidden bg-gray-lightest md:h-[85vh] md:w-[25rem]",
        isMobile() ? "" : inIframe() ? `rounded-[28px]` : "md:rounded-[28px]",
        "md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]",
      )}>
      {children}
    </div>
  );
}

window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});
